import { useCallback, useState } from 'react';
import { useFetchConfigurationItem } from '../../dependencies/useFetchConfigurationItem';
import { isMetadataElementInCanvas } from '../helper';
import { useRunOnce } from '../../../../../common/useRunOnce';
import {
  MetadataElementWithoutConnections,
  useAddMetadataElementToCanvas,
} from '../../../metadata-elements/useAddMetadataElementToCanvas';
import { telemetry } from '../../../../../../telemetry';
import { RelationshipsMenuView } from './RelationshipsMenuView';
import { MetadataRelationshipWithParentType } from './types';
import { useDocumentationApiFacade } from '../../../../../../apis/facades/useDocumentationApiFacade';
import { FIELD } from '@server/parser';
import { ConsumersOrDependencies } from '@server/documentation';
interface MetadataUsedByMenuProps {
  onClose: () => void;
  anchorEl: (EventTarget & HTMLLIElement) | null;
  funnelMap: FunnelMap;
  fullMetadataId: string;
  closeAll: () => void;
}

type ConsumersWithParentType = {
  [key: string]: MetadataRelationshipWithParentType[];
};

const stateMessages = {
  loading: 'Loading used by...',
  noRelationships: 'Element has no used by',
  allRelationshipsAdded: 'All used by added to the canvas',
  error: 'Error fetching used by. Please try again.',
  hasRelationships: 'Add used by to the canvas',
};

export const MetadataUsedByMenu = ({
  onClose,
  anchorEl,
  funnelMap,
  fullMetadataId,
  closeAll,
}: MetadataUsedByMenuProps) => {
  const [consumers, setConsumers] = useState<ConsumersWithParentType>({});
  const { crmOrgId, metadataId: originMetadataId } = funnelMap.metadataElements[fullMetadataId];
  const [state, setState] = useState<
    'loading' | 'error' | 'hasRelationships' | 'noRelationships' | 'allRelationshipsAdded'
  >('loading');

  const { get_crmOrgConfigurationConsumersFromSalto, get_crmOrgFieldConsumerFromSalto } =
    useDocumentationApiFacade();
  const { fetchConfigurationItem } = useFetchConfigurationItem();

  useRunOnce(async () => {
    try {
      const item = await fetchConfigurationItem(originMetadataId, crmOrgId);

      const consumers: ConsumersOrDependencies =
        item.parentType === FIELD
          ? await get_crmOrgFieldConsumerFromSalto({
              crmOrgId,
              objectName: item.objectApiNames[0] ?? '',
              fieldName: item.name ?? '',
              metadataId: item.metadataId,
            })
          : await get_crmOrgConfigurationConsumersFromSalto({
              crmOrgId,
              metadataId: item.metadataId,
            });

      Object.keys(consumers).forEach((key) => {
        if (key in consumers) {
          consumers[key] = consumers[key]?.map((item) => ({ ...item, parentType: key })) ?? [];
        }
      });

      // Filter all items to only include those that are not already in canvas
      const filteredConsumers: ConsumersWithParentType = Object.fromEntries(
        Object.entries(consumers)
          .map(([key, value]) => [
            key,
            value
              ?.filter(
                (item) =>
                  !isMetadataElementInCanvas({
                    metadataId: item.metadataId,
                    crmOrgId,
                    canvasFunnelMap: funnelMap,
                  }),
              )
              ?.map((item) => ({ ...item, parentType: key })) ?? [],
          ])
          .filter(([, value]) => value?.length > 0),
      );

      setConsumers(filteredConsumers);
      const _consumersCount = Object.values(consumers).flat().length;
      const filteredConsumersCount = Object.values(filteredConsumers).flat().length;

      if (filteredConsumersCount > 0) {
        setState('hasRelationships');
      } else {
        if (_consumersCount > 0) {
          setState('allRelationshipsAdded');
        } else {
          setState('noRelationships');
        }
      }
    } catch (error) {
      telemetry.captureError(error);
      console.error('Error fetching consumers:', error);
      setState('error');
    }
  });

  const { addMetadataElementsToCanvasAndEnrichConnections } = useAddMetadataElementToCanvas();

  const addConsumersToCanvas = useCallback(
    (consumers: MetadataRelationshipWithParentType[]) => {
      const metadataElements: MetadataElementWithoutConnections[] = consumers.map(
        (consumer, index) => ({
          name: consumer.label,
          type: consumer.type,
          crmOrgId,
          metadataId: consumer.metadataId,
          parentType: consumer.parentType,
          position: {
            column: 0,
            row: index,
          },
        }),
      );
      addMetadataElementsToCanvasAndEnrichConnections({
        metadataElements,
        canvasFunnelMap: funnelMap,
        commonChild: {
          metadataId: originMetadataId,
          crmOrgId,
        },
      });
      closeAll();
    },
    [
      addMetadataElementsToCanvasAndEnrichConnections,
      funnelMap,
      originMetadataId,
      crmOrgId,
      closeAll,
    ],
  );

  const entries = Object.entries(consumers).map(([key, value]) => ({
    key,
    data: value as MetadataRelationshipWithParentType[],
    count: value.length,
  }));

  return (
    <RelationshipsMenuView
      state={state}
      entries={entries}
      onEntryClick={addConsumersToCanvas}
      onClose={onClose}
      anchorEl={anchorEl}
      stateMessages={stateMessages}
    />
  );
};
